/* RESPONSIBLE TEAM: team-fin-voice */
import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { WavStreamPlayer } from '../helpers/wav-tools/wav-stream-player';
import { WavRecorder } from '../helpers/wav-tools/wav-recorder';
import { arrayBufferToBase64 } from '../helpers/wav-tools/array-buffer-to-base64';
import type IntlService from 'ember-intl/services/intl';
import { get } from 'embercom/lib/ajax';
import type {
  FinVoiceMessage,
  FinVoiceUserTranscriptError,
  FinVoiceToolCallDone,
  FinVoiceServerMedia,
  FinVoiceFinTranscriptDone,
  FinVoiceFinTranscriptDelta,
  FinVoiceUserTranscript,
  FinVoiceCallOutcomeServerEvent,
  FinVoiceEscalateToHumanServerEvent,
  FinVoiceErrorServerEvent,
  FinVoiceSearchStartedServerEvent,
  FinVoiceAnswerDeliveredServerEvent,
  FinVoiceDataConnectorCallStartedServerEvent,
  FinVoiceDataConnectorCallCompletedServerEvent,
  FinVoiceCodeEvaluationErrorServerEvent,
  FinVoiceContextUpdatedServerEvent,
  FinVoiceGuidanceTriggeredServerEvent,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-server-events';
import type { FinVoiceServerEvent } from 'embercom/objects/ai-agent/fin-voice/fin-voice-server-events';
import type {
  FinVoiceStartEvent,
  FinVoiceClientMediaMessage,
  FinVoiceMarkMessage,
  FinVoiceTextMessage,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-client-events';
import type {
  FinVoiceConversationEntryFunctionCall,
  FinVoiceConversationEntryMessage,
  FinVoiceConversationEntryCallOutcome,
  FinVoiceConversationEntryEscalateToHuman,
  FinVoiceConversationEntrySearch,
  FinVoiceConversationEntryDataConnectorCall,
  FinVoiceConversationEntryError,
  FinVoiceConversationEntryCodeEvaluationError,
  FinVoiceConversationEntryContextUpdated,
  FinVoiceConversationGuidanceTriggered,
  FinVoiceSession,
  FinVoiceConversationEntry,
  FinVoiceEvent,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-session';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type { FinVoicePipeline } from 'embercom/objects/ai-agent/fin-voice/fin-voice-pipeline-options';
import type {
  FinVoiceProcedure,
  FinVoiceRunbook,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import type { FinVoiceVoiceModel } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-model-options';
import type { FinVoiceVoiceLanguage } from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import { DEFAULT_VOICE_LANGUAGE } from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import { startSurvey } from 'embercom/lib/intercom-widget-helper';
import type {
  FinVoiceDefaultVadSettings,
  FinVoiceSemanticVadSettings,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-vad-settings';
import {
  DEFAULT_VAD_SETTINGS,
  stripDefaultVadValues,
} from 'embercom/objects/ai-agent/fin-voice/fin-voice-vad-settings';
import type FinVoiceConfigService from './fin-voice-config-service';
import { DEFAULT_VOICE } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import storage from 'embercom/vendor/intercom/storage';

const SAMPLE_RATE = 24000;
const MICROPHONE_STORAGE_KEY = 'fin-voice-selected-microphone';
const BUFFER_SIZE = 8192;

const SURVEY_ID = 45771922;

export type WebsocketState = 'connecting' | 'connected' | 'disconnected' | 'disconnecting';

export type AudioDeviceSettings = {
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  voiceIsolation: boolean;
};

const DEFAULT_GREETING = '';

export default class FinVoiceService extends Service {
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare finVoiceConfigService: FinVoiceConfigService;

  @tracked sessions: FinVoiceSession[] = [];
  @tracked websocket: WebSocket | null = null;
  @tracked wavStreamPlayer: WavStreamPlayer | null = null;
  @tracked wavRecorder: WavRecorder | null = null;
  @tracked websocketState: WebsocketState = 'disconnected';
  @tracked isMuted = true;
  @tracked verboseLogsEnabled = false;
  @tracked vadSettings: FinVoiceDefaultVadSettings = { ...DEFAULT_VAD_SETTINGS };
  @tracked semanticVadSettings: FinVoiceSemanticVadSettings = {
    type: 'semantic_vad',
    eagerness: 'high',
  };
  @tracked useSemanticVad = false;
  @tracked selectedNumberConfigurationPhoneNumber: string | undefined = undefined;
  @tracked selectedVoice: FinVoiceVoice = DEFAULT_VOICE;
  @tracked greeting = DEFAULT_GREETING;
  @tracked selectedPipeline: FinVoicePipeline = 'default';
  @tracked selectedRunbook: FinVoiceRunbook = 'none';
  @tracked selectedProcedure: FinVoiceProcedure | null = null;
  @tracked selectedVoiceModel: FinVoiceVoiceModel = 'eleven_turbo_v2';
  @tracked selectedLanguage: FinVoiceVoiceLanguage = DEFAULT_VOICE_LANGUAGE;
  @tracked cartesiaSpeed = 1.0;
  @tracked cartesiaVolume = 1.0;
  @tracked cartesiaEmotion = '';
  @tracked audioDeviceSettings: AudioDeviceSettings = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    // This is not supported on all browsers, but we can always send this since it will be ignored if not supported
    voiceIsolation: false,
  };
  @tracked availableMicrophones: Array<{
    deviceId: string;
    groupId: string;
    kind: 'audioinput';
    label: string;
    default: boolean;
  }> = [];
  @tracked selectedMicrophoneId: string | undefined = undefined;
  @tracked lowPassFilterCutoffHz = 4000;
  @tracked askForCsatRating = false;
  @tracked conversationDataAttributes: Record<string, string | number | boolean> = {};

  get defaultVoice(): FinVoiceVoice {
    if (this.appService.app.canUseFinVoiceDefaultVoiceLydia) {
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      return 'Lydia';
    }
    return DEFAULT_VOICE;
  }

  init() {
    super.init();
    this.selectedVoice = this.defaultVoice;
  }

  get isConnected() {
    return this.websocketState === 'connected';
  }

  get effectiveMicrophoneId(): string {
    return this.selectedMicrophoneId || 'default';
  }

  get activeVadSettings() {
    return this.useSemanticVad ? this.semanticVadSettings : this.vadSettings;
  }

  get sessionSettings() {
    let vadSettings =
      'type' in this.activeVadSettings
        ? this.activeVadSettings
        : stripDefaultVadValues(this.activeVadSettings);
    return {
      vadSettings,
      voice: this.selectedVoice,
      greeting: this.greeting,
      pipeline: this.selectedPipeline,
      ...(this.selectedRunbook !== 'none' && { runbook: this.selectedRunbook }),
      ...(this.selectedProcedure !== null && { procedureId: this.selectedProcedure.id }),
      voiceModel: this.selectedVoiceModel,
      language: this.selectedLanguage,
      ...(Object.keys(this.conversationDataAttributes).length > 0 && {
        conversationDataAttributes: this.conversationDataAttributes,
      }),
      ...(this.askForCsatRating && { askForCsat: this.askForCsatRating }),
      ...(this.selectedVoiceModel === 'sonic-3' && {
        voiceSettings: {
          cartesia: {
            speed: this.cartesiaSpeed,
            volume: this.cartesiaVolume,
            ...(this.cartesiaEmotion && { emotion: this.cartesiaEmotion }),
          },
        },
      }),
    };
  }

  async reportIssue() {
    await this.copyLatestSessionId(false, false);
    startSurvey(SURVEY_ID);
  }

  clearAllSessions() {
    this.sessions = [];
  }

  async copyLatestSessionId(includeAppId = true, showNotification = true) {
    if (this.sessions.length === 0) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.no-sessions-message'),
      );
      return;
    }

    let session = this.sessions[this.sessions.length - 1];
    if (!session) {
      return;
    }

    let appId = this.appService.app.id;

    let copyText = includeAppId
      ? `app_id: ${appId}\nstream_id: ${session.sessionId}`
      : session.sessionId;

    await navigator.clipboard.writeText(copyText);
    if (showNotification) {
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.voice-playground.session-details-copied'),
      );
    }
  }

  async copyTranscript() {
    if (this.sessions.length === 0) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.no-sessions-message'),
      );
      return;
    }

    let transcriptLines: string[] = [];

    for (let session of this.sessions) {
      for (let message of session.conversation.messages) {
        if (message.type === 'message' && message.transcript) {
          let timestamp = new Date(message.timestamp).toLocaleTimeString();
          let speaker =
            message.role === 'user'
              ? this.intl.t('ai-agent.voice-playground.transcript-speaker-customer')
              : this.intl.t('ai-agent.voice-playground.transcript-speaker-fin');
          transcriptLines.push(`[${timestamp}] ${speaker}: ${message.transcript}`);
        }
      }
    }

    await navigator.clipboard.writeText(transcriptLines.join('\n'));
    this.notificationsService.notifyConfirmation(
      this.intl.t('ai-agent.voice-playground.transcript-copied'),
    );
  }

  async connectStream(): Promise<void> {
    try {
      this.websocketState = 'connecting';
      this.sessions = [
        ...this.sessions,
        {
          sessionId: `stream_${crypto.randomUUID()}`,
          sessionStartTimestamp: Date.now(),
          sessionEndTimestamp: null,
          sessionError: null,
          events: [],
          conversation: {
            messages: [],
          },
        },
      ];

      await this._setupAudioTools();

      this.websocket = await this._createWebSocket();
      this._setupWebsocketHandlers();
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.failed-to-connect-message'),
      );
      console.error('Failed to connect to voice server', error);

      await this._onConnectionClose();

      this._updateLastSession('sessionError', error.message || error.toString());
    }
  }

  async disconnectStream(): Promise<void> {
    if (!this.websocket) {
      return;
    }

    this.websocketState = 'disconnecting';

    await this._endWavRecorder();
    await this._endWavStreamPlayer();
    this.websocket.close(1000);
  }

  getFrequencyData() {
    if (!this.wavRecorder || !this.wavRecorder.recording || this.isMuted) {
      // Max number that can be shown in the visualiser with width of 256px, 2px wide bars and 4px spacing between them
      let maxBars = 42;
      return { values: new Float32Array(maxBars).fill(0) };
    }

    return this.wavRecorder.getFrequencies();
  }

  selectNumberConfiguration(phoneNumber: string | undefined) {
    if (!phoneNumber) {
      this.selectedNumberConfigurationPhoneNumber = undefined;
      this.selectedVoice = this.defaultVoice;
      this.greeting = DEFAULT_GREETING;
      return;
    }

    this.selectedNumberConfigurationPhoneNumber = phoneNumber;
    this.resetNumberConfiguration();
  }

  resetNumberConfiguration() {
    if (!this.selectedNumberConfigurationPhoneNumber) {
      return;
    }

    let config =
      this.finVoiceConfigService.numberConfigurations[this.selectedNumberConfigurationPhoneNumber];

    this.selectedVoice = config?.voice || this.defaultVoice;
    this.greeting = config?.greeting || DEFAULT_GREETING;

    let locale = (this.finVoiceConfigService.phoneNumbers[config?.workspacePhoneNumberId]?.locale ||
      DEFAULT_VOICE_LANGUAGE) as FinVoiceVoiceLanguage;

    this.updateLanguage(locale);

    this.selectedRunbook = config?.runbook || 'none';
  }

  async deployNumberConfiguration() {
    if (!this.selectedNumberConfigurationPhoneNumber) {
      return;
    }

    try {
      await this.finVoiceConfigService.updateVoiceBehavior(
        this.selectedNumberConfigurationPhoneNumber,
        this.selectedVoice,
        this.greeting,
      );
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.voice-playground.settings.number-configuration-deployed'),
      );
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.settings.failed-to-deploy-number-configuration'),
      );
    }
  }

  async _createWebSocket(): Promise<WebSocket> {
    let params: Record<string, string> = {
      app_id: this.appService.app.id,
    };

    if (this.selectedNumberConfigurationPhoneNumber) {
      params.config_id =
        this.finVoiceConfigService.numberConfigurations[
          this.selectedNumberConfigurationPhoneNumber
        ].id;
    }

    let { ws_url } = await get('/ember/fin_voice_playground/start_session', params);

    return new WebSocket(ws_url);
  }

  async _setupAudioTools() {
    await Promise.all([this._connectWavStreamPlayer(), this._connectWavRecorder()]);
  }

  _setupWebsocketHandlers() {
    if (!this.websocket) {
      throw new Error('WebSocket not initialized');
    }

    this.websocket.onopen = async () => {
      await this._onConnectionOpen();
    };

    this.websocket.onclose = (event) => {
      if (!event.wasClean) {
        console.error('WebSocket connection closed unexpectedly', event.code, event.reason);
      }

      this._onConnectionClose();
    };

    this.websocket.onmessage = (event) => {
      this._onMessage(event);
    };

    this.websocket.onerror = (error) => {
      console.error('An error occurred on the websocket', error);
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.websocket-error-message'),
      );
      this._updateLastSession(
        'sessionError',
        this.intl.t('ai-agent.voice-playground.websocket-error-message'),
      );
      this._onConnectionClose();
    };
  }

  async _onConnectionOpen() {
    try {
      if (!this.websocket) {
        return;
      }

      let session = this.sessions[this.sessions.length - 1];
      let sessionId = session.sessionId;

      let startMessage: FinVoiceStartEvent = {
        direction: 'client',
        event: 'start',
        event_id: this._generateEventId(),
        timestamp: Date.now(),
        start: {
          streamSid: sessionId,
          ...this.sessionSettings,
        },
      };

      this.websocket.send(JSON.stringify(startMessage));
      this._addConversationItemToSession({
        itemId: startMessage.event_id,
        previousItemId: null,
        status: 'completed',
        timestamp: startMessage.timestamp,
        type: 'start',
        streamId: sessionId,
        vadSettings: this.activeVadSettings,
        voice: this.selectedVoice,
        greeting: this.greeting,
        pipeline: this.selectedPipeline,
        ...(this.selectedRunbook !== 'none' && { runbook: this.selectedRunbook }),
        ...(this.selectedProcedure !== null && { procedureId: this.selectedProcedure.id }),
        voiceModel: this.selectedVoiceModel,
      });
      this._addEventToSession(startMessage);

      await this._startWavRecorder();

      this.websocketState = 'connected';
    } catch (error) {
      console.error('Failed to start session', error);

      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.error-on-starting-session'),
      );

      this._updateLastSession('sessionError', error.message || error.toString());

      this.websocket?.close();
    }
  }

  async _onConnectionClose() {
    await this._endWavRecorder();
    await this._endWavStreamPlayer();

    this.wavRecorder = null;
    this.wavStreamPlayer = null;
    this.websocket = null;

    this.websocketState = 'disconnected';

    this._updateLastSession('sessionEndTimestamp', Date.now());
  }

  _onMessage(message: MessageEvent) {
    let data: FinVoiceServerEvent = JSON.parse(message.data) as FinVoiceServerEvent;
    this.handleServerEvent(data, false);
  }

  /**
   * Routes a parsed server event through the appropriate handler.
   * Called by both _onMessage (internal WebSocket) and handleExternalServerEvent (React bridge).
   * When isExternal is true, media/clear are skipped since React already handles audio playback.
   */
  private handleServerEvent(data: FinVoiceServerEvent, isExternal: boolean) {
    data.direction = 'server';

    // If we don't have a timestamp, set it to when we received the message
    if (!data.timestamp) {
      data.timestamp = Date.now();
    }

    switch (data.event) {
      case 'media': {
        if (!isExternal) {
          this._onMedia(data);
        }
        this._addEventToSession({
          ...data,
          media: '[payload]',
        });
        break;
      }
      case 'clear':
        if (!isExternal) {
          this._onClear();
        }
        this._addEventToSession(data);
        break;
      case 'create_conversation_item':
        this._onCreateConversationItem(data);
        this._addEventToSession(data);
        break;
      case 'user_transcript_item':
        this._onUserTranscriptItem(data);
        this._addEventToSession(data);
        break;
      case 'user_transcript_error':
        this._onUserTranscriptError(data);
        this._addEventToSession(data);
        break;
      case 'fin_transcript_item_delta':
        this._onFinTranscriptDelta(data);
        break;
      case 'fin_transcript_item_done':
        this._onFinTranscriptDone(data);
        this._addEventToSession(data);
        break;
      case 'tool_call_done':
        this._onToolCallDone(data);
        this._addEventToSession(data);
        break;
      case 'call_outcome':
        this._onCallOutcome(data);
        this._addEventToSession(data);
        break;
      case 'escalate_to_human':
        this._onEscalateToHuman(data);
        this._addEventToSession(data);
        break;
      case 'error':
        this._onErrorEvent(data);
        this._addEventToSession(data);
        break;
      case 'search_started':
        this._onSearchStarted(data);
        this._addEventToSession(data);
        break;
      case 'answer_delivered':
        this._onAnswerDelivered(data);
        this._addEventToSession(data);
        break;
      case 'data_connector_call_started':
        this._onDataConnectorCallStarted(data);
        this._addEventToSession(data);
        break;
      case 'data_connector_call_completed':
        this._onDataConnectorCallCompleted(data);
        this._addEventToSession(data);
        break;
      case 'code_evaluation.error':
        this._onCodeEvaluationError(data);
        this._addEventToSession(data);
        break;
      case 'context_updated':
        this._onContextUpdated(data);
        this._addEventToSession(data);
        break;
      case 'guidance_triggered':
        this._onGuidanceTriggered(data);
        this._addEventToSession(data);
        break;
      default:
        data.event = data.event || 'unknown_event';
        this._addEventToSession(data);
        break;
    }
  }

  _onMedia(data: FinVoiceServerMedia) {
    if (!data.media?.payload) {
      return;
    }

    let audioData = new Uint8Array(
      atob(data.media.payload)
        .split('')
        .map((c) => c.charCodeAt(0)),
    );
    this.wavStreamPlayer?.add16BitPCM(
      audioData.buffer,
      data.mark,
      data.mark ? this._handleChunkPlayed.bind(this) : undefined,
    );
  }

  _onClear() {
    this.wavStreamPlayer?.interrupt();
  }

  _onUserTranscriptItem(data: FinVoiceUserTranscript) {
    let session = this.sessions[this.sessions.length - 1];

    let message = session.conversation.messages.find((message) => message.itemId === data.item_id);

    if (message && message.type === 'message') {
      // If we get back a transcript that is empty, we should set the status to error
      if (data.transcript === '') {
        message.transcript = this.intl.t(
          'ai-agent.voice-playground.transcription-error-code-audio-unintelligible',
        );
        message.status = 'error';
        message.errorCode = 'audio_unintelligible';
      } else {
        message.transcript = data.transcript;
        message.status = 'completed';
      }
    }

    this.sessions = [...this.sessions];
  }

  _onUserTranscriptError(data: FinVoiceUserTranscriptError) {
    let session = this.sessions[this.sessions.length - 1];

    let message = session.conversation.messages.find((message) => message.itemId === data.item_id);

    if (!session || !message || message.type !== 'message') {
      return;
    }

    message.status = 'error';
    message.errorCode = data.error.code;

    if (data.error.code === 'audio_unintelligible') {
      message.transcript = this.intl.t(
        'ai-agent.voice-playground.transcription-error-code-audio-unintelligible',
      );
    } else {
      message.transcript = this.intl.t('ai-agent.voice-playground.transcription-error');
    }

    this.sessions = [...this.sessions];
  }

  _onFinTranscriptDelta(data: FinVoiceFinTranscriptDelta) {
    let session = this.sessions[this.sessions.length - 1];
    let message = session.conversation.messages.find((message) => message.itemId === data.item_id);

    if (message && message.type === 'message') {
      // If the message was loading before we should clear the transcript
      if (message.status === 'loading') {
        message.status = 'in_progress';
        message.transcript = '';
      }

      message.transcript += data.delta;
    }

    this.sessions = [...this.sessions];
  }

  _onFinTranscriptDone(data: FinVoiceFinTranscriptDone) {
    let session = this.sessions[this.sessions.length - 1];
    let message = session.conversation.messages.find((message) => message.itemId === data.item_id);

    if (message && message.type === 'message') {
      message.transcript = data.transcript;
      message.status = 'completed';
    }

    this.sessions = [...this.sessions];
  }

  _onToolCallDone(data: FinVoiceToolCallDone) {
    let session = this.sessions[this.sessions.length - 1];
    let message = session.conversation.messages.find((message) => message.itemId === data.item_id);

    if (message && message.type === 'function_call') {
      message.status = 'completed';
      message.toolArguments = data.arguments;
    }

    this.sessions = [...this.sessions];
  }

  _onCallOutcome(data: FinVoiceCallOutcomeServerEvent) {
    let callOutcomeEntry: FinVoiceConversationEntryCallOutcome = {
      itemId: data.event_id,
      previousItemId: null,
      status: 'completed',
      timestamp: data.timestamp,
      type: 'call_outcome',
      outcome: data.payload.outcome,
      reason: data.payload.reason,
      conversationId: data.payload.conversation_id,
      adminId: data.payload.admin_id,
      conversationSummary: data.payload.conversation_summary,
      customDataAttributes: data.payload.custom_data_attributes,
      transcriptTurns: data.payload.transcript.turns,
      intentClassification: data.payload.intent_classification,
    };

    this._addConversationItemToSession(callOutcomeEntry);
  }

  _onEscalateToHuman(data: FinVoiceEscalateToHumanServerEvent) {
    let escalateEntry: FinVoiceConversationEntryEscalateToHuman = {
      itemId: data.event_id,
      previousItemId: null,
      status: 'completed',
      timestamp: data.timestamp,
      type: 'escalate_to_human',
      escalationContext: data.payload.escalation_context,
      customDataAttributes: data.payload.custom_data_attributes,
      transcriptTurns: data.payload.transcript.turns,
      intentClassification: data.payload.intent_classification,
    };

    this._addConversationItemToSession(escalateEntry);
  }

  _onErrorEvent(data: FinVoiceErrorServerEvent) {
    let errorEntry: FinVoiceConversationEntryError = {
      itemId: data.event_id,
      previousItemId: null,
      status: 'error',
      timestamp: data.timestamp,
      type: 'error',
      error: data.payload.error || data.payload.message || data.payload.error_type,
      errorType: data.payload.error_type,
      instructionIndex: data.payload.instruction_index,
      instructionType: data.payload.instruction_type,
      instructionLabel: data.payload.instruction_label,
    };

    this._addConversationItemToSession(errorEntry);
  }

  _onSearchStarted(data: FinVoiceSearchStartedServerEvent) {
    let searchEntry: FinVoiceConversationEntrySearch = {
      itemId: data.payload.search_id,
      previousItemId: null,
      status: 'in_progress',
      timestamp: data.timestamp,
      type: 'search',
      searchId: data.payload.search_id,
      query: data.payload.query,
      articleSources: [],
      triggeredGuidance: [],
    };

    this._addConversationItemToSession(searchEntry);
  }

  _onAnswerDelivered(data: FinVoiceAnswerDeliveredServerEvent) {
    let session = this.sessions[this.sessions.length - 1];

    let entry = session?.conversation.messages.find(
      (message) => message.type === 'search' && message.searchId === data.payload.search_id,
    );

    if (entry && entry.type === 'search') {
      entry.status = 'completed';
      entry.query = data.payload.query;
      entry.articleSources = data.payload.article_sources;
      entry.triggeredGuidance = data.payload.triggered_guidance;
    }

    this.sessions = [...this.sessions];
  }

  _onDataConnectorCallStarted(data: FinVoiceDataConnectorCallStartedServerEvent) {
    let entry: FinVoiceConversationEntryDataConnectorCall = {
      itemId: data.payload.action_id,
      previousItemId: null,
      status: 'loading',
      timestamp: data.timestamp,
      type: 'data_connector_call',
      actionName: data.payload.action_name,
      actionId: data.payload.action_id,
    };

    this._addConversationItemToSession(entry);
  }

  _onDataConnectorCallCompleted(data: FinVoiceDataConnectorCallCompletedServerEvent) {
    let session = this.sessions[this.sessions.length - 1];

    let entry = session?.conversation.messages.find(
      (message) =>
        message.type === 'data_connector_call' && message.actionId === data.payload.action_id,
    );

    if (entry && entry.type === 'data_connector_call') {
      entry.status = data.payload.result_status === 'ok' ? 'completed' : 'error';
      entry.requestParams = data.payload.request_params;
      entry.resultStatus = data.payload.result_status;
      entry.resultError = data.payload.result_error;
      entry.resultUpdates = data.payload.result_updates;
      entry.durationMs = data.payload.duration_ms;
    }

    this.sessions = [...this.sessions];
  }

  _onCodeEvaluationError(data: FinVoiceCodeEvaluationErrorServerEvent) {
    let entry: FinVoiceConversationEntryCodeEvaluationError = {
      itemId: data.event_id,
      previousItemId: null,
      status: 'error',
      timestamp: data.timestamp,
      type: 'code_evaluation_error',
      instructionIndex: data.payload.instruction_index,
      instructionType: data.payload.instruction_type,
      fieldName: data.payload.field_name,
      codeExpression: data.payload.code_expression,
      inputsSnapshot: data.payload.inputs_snapshot,
      error: data.payload.error,
      suggestion: data.payload.suggestion,
    };

    this._addConversationItemToSession(entry);
  }

  _onContextUpdated(data: FinVoiceContextUpdatedServerEvent) {
    let entry: FinVoiceConversationEntryContextUpdated = {
      itemId: data.event_id,
      previousItemId: null,
      status: 'completed',
      timestamp: data.timestamp,
      type: 'context_updated',
      source: data.payload.source,
      updates: data.payload.updates,
    };

    this._addConversationItemToSession(entry);
  }

  _onGuidanceTriggered(data: FinVoiceGuidanceTriggeredServerEvent) {
    let entry: FinVoiceConversationGuidanceTriggered = {
      itemId: data.event_id,
      previousItemId: null,
      status: 'completed',
      timestamp: data.timestamp,
      type: 'guidance_triggered',
      userIntent: data.payload.user_intent,
      rule_id: data.payload.rule_id,
      rule_text: data.payload.rule_text,
      match_type: data.payload.match_type,
      title: data.payload.title,
      guideline_id: data.payload.guideline_id,
    };

    this._addConversationItemToSession(entry);
  }

  _onCreateConversationItem(data: FinVoiceMessage) {
    let type = data.item.type;

    switch (data.item.type) {
      case 'message': {
        let status: 'loading' | 'completed' = 'loading';
        let transcript = this.intl.t('ai-agent.voice-playground.transcript-in-progress');

        if (data.item.content?.[0]) {
          let content = data.item.content[0];

          if (content.type === 'input_text') {
            status = 'completed';
            transcript = content.text;
          } else if (content.type === 'input_audio' && content.transcript) {
            status = 'completed';
            transcript = content.transcript;
          }
        }

        let message: FinVoiceConversationEntryMessage = {
          itemId: data.item.id,
          previousItemId: data.previous_item_id,
          role: data.item.role,
          status,
          transcript,
          timestamp: data.timestamp,
          type: 'message',
        };

        this._addConversationItemToSession(message);

        break;
      }
      case 'function_call': {
        let message: FinVoiceConversationEntryFunctionCall = {
          itemId: data.item.id,
          previousItemId: data.previous_item_id,
          status: 'loading',
          timestamp: data.timestamp,
          type: 'function_call',
          toolName: data.item.name,
          toolCallId: data.item.call_id,
          toolArguments: data.item.arguments,
        };

        this._addConversationItemToSession(message);

        break;
      }
      case 'function_call_output': {
        let session = this.sessions[this.sessions.length - 1];

        let callId = data.item.call_id;

        let message = session.conversation.messages.find(
          (message) => message.type === 'function_call' && message.toolCallId === callId,
        );

        if (message && message.type === 'function_call') {
          message.toolOutput = data.item.output;
        }

        break;
      }
      default:
        console.error('Unknown item type', type);
        break;
    }

    this.sessions = [...this.sessions];
  }

  async _connectWavStreamPlayer() {
    this.wavStreamPlayer = new WavStreamPlayer({ sampleRate: SAMPLE_RATE });
    await this.wavStreamPlayer.connect();
  }

  async _connectWavRecorder() {
    this.wavRecorder = new WavRecorder({
      sampleRate: SAMPLE_RATE,
      audioDeviceSettings: this.audioDeviceSettings,
      enableLowPassFilter: this.appService.app.canUseFinVoiceLowPassFilter,
      lowPassFilterCutoffHz: this.lowPassFilterCutoffHz,
    });

    // Use effectiveMicrophoneId to support user-selected microphone
    // Falls back to 'default' if no microphone is selected
    await this.wavRecorder.begin(this.effectiveMicrophoneId);
  }

  async _startWavRecorder() {
    if (!this.wavRecorder) {
      console.error('Wav recorder not initialized');
      return;
    }

    await this.wavRecorder.record(this._handleAudioData.bind(this), BUFFER_SIZE);
    this.isMuted = false;
  }

  muteAudio() {
    this.isMuted = true;
  }

  unmuteAudio() {
    this.isMuted = false;
  }

  sendTextMessage(message: string) {
    if (!this.websocket) {
      console.error('WebSocket not initialized');
      return;
    }

    let textMessage: FinVoiceTextMessage = {
      direction: 'client',
      event_id: this._generateEventId(),
      event: 'text',
      text: message,
      timestamp: Date.now(),
    };

    this.websocket.send(JSON.stringify(textMessage));
    this._addEventToSession(textMessage);
  }

  _handleAudioData(data: { mono: ArrayBuffer }) {
    if (data.mono.byteLength === 0) {
      return;
    }

    if (!this.websocket) {
      console.error('WebSocket not initialized');
      return;
    }

    let mono = data.mono;

    // TODO after event loop changes see if we can use built in pause.
    if (this.isMuted) {
      // Create a view of the audio data as Int16Array and set all samples to 0
      let view = new Int16Array(mono);
      view.fill(0);
      mono = view.buffer;
    }

    let audioBuffer = arrayBufferToBase64(mono);

    let mediaMessage: FinVoiceClientMediaMessage = {
      direction: 'client',
      event_id: this._generateEventId(),
      event: 'media',
      media: {
        payload: audioBuffer,
        timestamp: Date.now(),
      },
      timestamp: Date.now(),
    };

    this.websocket.send(JSON.stringify(mediaMessage));
  }

  _handleChunkPlayed(trackId: string) {
    if (!this.websocket) {
      console.error('WebSocket not initialized');
      return;
    }

    if (!trackId) {
      console.error('Track ID is required');
      return;
    }

    let markMessage: FinVoiceMarkMessage = {
      direction: 'client',
      event: 'mark',
      event_id: this._generateEventId(),
      timestamp: Date.now(),
      mark: {
        name: trackId,
      },
    };

    this.websocket.send(JSON.stringify(markMessage));
  }

  _generateEventId() {
    return `evt_${crypto.randomUUID()}`;
  }

  async _endWavRecorder() {
    if (this.wavRecorder && this.wavRecorder.recording) {
      await this.wavRecorder.end();
    }

    this.isMuted = true;
  }

  async _endWavStreamPlayer() {
    if (this.wavStreamPlayer) {
      await this.wavStreamPlayer.interrupt();
      await this.wavStreamPlayer.end();
    }
  }

  _addEventToSession(event: FinVoiceEvent) {
    let session = this.sessions[this.sessions.length - 1];
    session.events = [...session.events, event];
    this.sessions = [...this.sessions];
  }

  _addConversationItemToSession(message: FinVoiceConversationEntry) {
    let session = this.sessions[this.sessions.length - 1];

    if (session) {
      session.conversation.messages = [...session.conversation.messages, message];
    }

    this.sessions = [...this.sessions];
  }

  _updateLastSession<K extends keyof Pick<FinVoiceSession, 'sessionEndTimestamp' | 'sessionError'>>(
    key: K,
    value: Pick<FinVoiceSession, K>[K],
  ) {
    let session = this.sessions[this.sessions.length - 1];

    if (session) {
      session[key] = value;
      this.sessions = [...this.sessions];
    }
  }

  updateUseSemanticVad(value: boolean): void {
    this.useSemanticVad = value;
  }

  updateEagerness(value: string): void {
    let eagerness = value as 'low' | 'medium' | 'high' | 'auto';
    this.semanticVadSettings = {
      ...this.semanticVadSettings,
      eagerness,
    };
  }

  updateLanguage(language: FinVoiceVoiceLanguage): void {
    this.selectedLanguage = language;
    this.selectedVoiceModel = language === 'en-US' ? 'eleven_turbo_v2' : 'eleven_turbo_v2_5';
  }

  updateConversationDataAttributes(attrs: Record<string, string | number | boolean>): void {
    this.conversationDataAttributes = attrs;
  }

  updateRunbook(runbook: FinVoiceRunbook): void {
    this.selectedRunbook = runbook;
  }

  updateProcedure(procedure: FinVoiceProcedure): void {
    this.selectedProcedure = procedure;
  }

  async loadAvailableMicrophones(): Promise<void> {
    try {
      if (!navigator.mediaDevices || !('enumerateDevices' in navigator.mediaDevices)) {
        console.error('Could not request user devices');
        this.availableMicrophones = [];
        return;
      }

      // Request permission first by getting a temporary stream
      try {
        let stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
      } catch (e) {
        console.error('Microphone permission denied', e);
      }

      // Enumerate all audio input devices
      let allDevices = await navigator.mediaDevices.enumerateDevices();
      let audioInputs = allDevices.filter((device) => device.kind === 'audioinput');

      // Find which device is the current default (by matching groupId with the 'default' pseudo-device)
      let defaultPseudoDevice = audioInputs.find((d) => d.deviceId === 'default');
      let defaultGroupId = defaultPseudoDevice?.groupId;

      // Build device list, excluding the 'default' pseudo-device but marking the actual default
      // Note: MediaDeviceInfo properties aren't enumerable, so we must explicitly copy them
      let devices = audioInputs
        .filter((device) => device.deviceId !== 'default')
        .map((device) => ({
          deviceId: device.deviceId,
          groupId: device.groupId,
          kind: device.kind as 'audioinput',
          label: device.label,
          default: device.groupId === defaultGroupId,
        }));

      this.availableMicrophones = devices;

      // Load persisted selection from localStorage
      let savedMicrophoneId = storage.get(MICROPHONE_STORAGE_KEY);
      if (savedMicrophoneId && devices.some((d) => d.deviceId === savedMicrophoneId)) {
        this.selectedMicrophoneId = savedMicrophoneId;
      }
    } catch (error) {
      console.error('Failed to load available microphones', error);
      this.availableMicrophones = [];
    }
  }

  selectMicrophone(deviceId: string | undefined): void {
    this.selectedMicrophoneId = deviceId;
    if (deviceId) {
      storage.set(MICROPHONE_STORAGE_KEY, deviceId);
    } else {
      storage.remove(MICROPHONE_STORAGE_KEY);
    }
  }

  // --- External session management (React controls bridge) ---

  createExternalSession(sessionId: string) {
    this.sessions = [
      ...this.sessions,
      {
        sessionId,
        sessionStartTimestamp: Date.now(),
        sessionEndTimestamp: null,
        sessionError: null,
        events: [],
        conversation: {
          messages: [],
        },
      },
    ];
  }

  endExternalSession(error?: string) {
    if (error) {
      this._updateLastSession('sessionError', error);
    }
    this._updateLastSession('sessionEndTimestamp', Date.now());
    this.websocketState = 'disconnected';
  }

  handleExternalServerEvent(event: FinVoiceServerEvent) {
    this.handleServerEvent(event, true);
  }

  async fetchProcedures(appId: string): Promise<FinVoiceProcedure[]> {
    if (!this.appService.app.canUseFeature('fin-voice-procedures')) {
      return [];
    }

    let response = await get('/ember/fin_voice/procedures', {
      app_id: appId,
    });
    return response as FinVoiceProcedure[];
  }
}

declare module '@ember/service' {
  interface Registry {
    finVoiceService: FinVoiceService;
    'fin-voice-service': FinVoiceService;
  }
}
